<template>
  <div class="column-edit-view-wrapper">
    <template v-if="valueField.useExpression && expressionTokens.length">
      <span class="vf-display-expr">
        <span
          v-for="(t, i) in expressionTokens"
          :key="i"
          class="vf-display-token"
          :class="tokenClass(t)"
        >
          {{ tokenText(t) }}
        </span>
      </span>
      <a-tag
        v-if="!exprValid"
        color="error"
        class="vf-display-warn"
      >
        {{ exprError || $t('表达式不完整') }}
      </a-tag>
    </template>
    <span
      v-else
      class="pivot-zone-item-label"
    >
      {{ fieldLabel }}
    </span>

    <!-- 隐藏徽标：该值字段仅参与聚合计算，不在透视表/图中展示 -->
    <a-tag
      v-if="valueField.hidden"
      class="vf-display-hidden"
      color="default"
    >
      {{ $t('隐藏') }}
    </a-tag>

    <!-- 过滤条件徽标 -->
    <template v-if="filterTokens.length">
      <a-tag
        class="vf-display-filter"
        :color="filterValid ? 'blue' : 'error'"
      >
        ⚑ {{ filterPreview }}
      </a-tag>
      <a-tag
        v-if="!filterValid"
        color="error"
        class="vf-display-warn"
      >
        {{ filterError || $t('过滤条件不完整') }}
      </a-tag>
    </template>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue';
import { PivotValueField, PivotSubExpression, PivotAggComputedRefOption } from '../typing';
import { validateValueExpression, ValueExprValidationOptions } from '../utils';

interface Props {
  valueField: PivotValueField;
  fieldOptions?: { field: string; label: string }[];
  // 当前配置中全部子表达式（供按 id 解析 subexpr token 的显示名）
  subExpressions?: PivotSubExpression[];
  // 已配置的值字段（聚合后的值），供按 id 解析 value token 的显示名。
  // 仅「聚合计算值」卡片预览需要传入；普通值字段表达式不含 value token。
  valueFieldOptions?: { id: string; label: string }[];
  // 已配置的聚合计算值，供按 id 解析 aggcomputed token 的显示名。
  // 仅「聚合计算值」卡片预览需要传入；普通值字段表达式不含 aggcomputed token。
  aggComputedOptions?: PivotAggComputedRefOption[];
}
const props = defineProps<Props>();

const fieldLabel = computed(() => props.valueField.label || props.valueField.field || '');

const expressionTokens = computed(() => props.valueField.expression || []);
const filterTokens = computed(() => props.valueField.filter || []);

const labelOf = (field: string) => {
  const f = (props.fieldOptions || []).find(x => x.field === field);
  return f ? f.label : field;
};

const subExprNameOf = (id: string) => {
  const s = (props.subExpressions || []).find(x => x.id === id);
  return s ? s.name : id;
};

// value token（聚合后的值字段引用）→ 显示名；找不到时回退为 id
const valueFieldNameOf = (id: string) => {
  const v = (props.valueFieldOptions || []).find(x => x.id === id);
  return v ? v.label : id;
};

// aggcomputed token（聚合计算值嵌套引用）→ 显示名；找不到时回退为 id
const aggComputedNameOf = (id: string) => {
  const a = (props.aggComputedOptions || []).find(x => x.id === id);
  return a ? a.label : id;
};

const opDisplay: Record<string, string> = {
  '*': '×',
  '/': '÷',
  '&&': '且',
  '||': '或',
  '>=': '≥',
  '<=': '≤',
  '==': '=',
  '!=': '≠',
  '?': '?',
  ':': ':',
};

const tokenText = (t: { type: string; value: string }): string => {
  switch (t.type) {
    case 'field':
      return `${labelOf(t.value)}(${t.value})`;
    case 'operator':
      return opDisplay[t.value] || t.value;
    case 'number':
      return t.value;
    case 'string':
      return `'${t.value}'`;
    case 'bool':
      return t.value;
    case 'lparen':
      return '(';
    case 'rparen':
      return ')';
    case 'subexpr':
      return subExprNameOf(t.value);
    case 'value':
      return `[${valueFieldNameOf(t.value)}]`;
    case 'aggcomputed':
      return `{{${aggComputedNameOf(t.value)}}}`;
    default:
      return '';
  }
};

const tokenClass = (t: { type: string }) => {
  if (t.type === 'field') return 'vf-token-field';
  if (t.type === 'number') return 'vf-token-number';
  if (t.type === 'string') return 'vf-token-string';
  if (t.type === 'bool') return 'vf-token-bool';
  if (t.type === 'lparen' || t.type === 'rparen') return 'vf-token-paren';
  if (t.type === 'subexpr') return 'vf-token-subexpr';
  if (t.type === 'value') return 'vf-token-value';
  if (t.type === 'aggcomputed') return 'vf-token-aggcomputed';
  return 'vf-token-op';
};

// 校验透传聚合计算值引用选项：预览卡片上的 aggcomputed token 同样做存在性/自引用/环形校验
const validationOptions = (): ValueExprValidationOptions => ({
  aggComputedRefs: props.aggComputedOptions,
  excludeAggComputedId: props.valueField?.id,
});
const exprValid = computed(() => validateValueExpression(props.valueField.expression, validationOptions()).valid);
const exprError = computed(() => validateValueExpression(props.valueField.expression, validationOptions()).message || '');

const filterValid = computed(() => validateValueExpression(props.valueField.filter).valid);
const filterError = computed(() => validateValueExpression(props.valueField.filter).message || '');
const filterPreview = computed(() =>
  filterTokens.value
    .map(t => {
      if (t.type === 'field') return labelOf(t.value);
      if (t.type === 'operator') return opDisplay[t.value] || t.value;
      if (t.type === 'number') return t.value;
      if (t.type === 'string') return `'${t.value}'`;
      if (t.type === 'bool') return t.value;
      if (t.type === 'lparen') return '(';
      if (t.type === 'rparen') return ')';
      if (t.type === 'subexpr') return subExprNameOf(t.value);
      if (t.type === 'value') return `[${valueFieldNameOf(t.value)}]`;
      if (t.type === 'aggcomputed') return `{{${aggComputedNameOf(t.value)}}}`;
      return '';
    })
    .join(' '),
);
</script>

<style lang="less" scoped>
.pivot-zone-item-label {
  flex: 0 1 auto;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  flex-shrink: 0;
}

.vf-display-expr {
  display: inline-flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 2px;
}

.vf-display-token {
  padding: 0 4px;
  border-radius: 3px;
  font-size: 12px;
  &.vf-token-field {
    color: #0958d9;
    background: #e6f7ff;
  }
  &.vf-token-op {
    font-weight: 700;
    color: #333;
  }
  &.vf-token-number {
    color: #389e0d;
    background: #f6ffed;
  }
  &.vf-token-string {
    color: #c41d7f;
    background: #fff0f6;
  }
  &.vf-token-bool {
    color: #722ed1;
    background: #f9f0ff;
  }
  &.vf-token-paren {
    font-weight: 700;
    color: #d46b08;
  }
  &.vf-token-subexpr {
    color: #722ed1;
    background: #f9f0ff;
  }
  // 聚合值引用（value token）：与表达式编辑器的 vee-token-value 保持同色系
  &.vf-token-value {
    color: #08979c;
    background: #e6fffb;
  }
  // 聚合计算值嵌套引用（aggcomputed token）：与表达式编辑器的 vee-token-aggcomputed 保持同色系
  &.vf-token-aggcomputed {
    color: #2f54eb;
    background: #f0f5ff;
  }
}

.vf-display-filter {
  margin-left: 4px;
}

.vf-display-hidden {
  margin-left: 4px;
  color: #8c8c8c;
  background: #fafafa;
  border-color: #d9d9d9;
}

.vf-display-warn {
  margin-left: 4px;
}
</style>
